import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Bell, CheckCheck, Inbox, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { SectionBar, SectionPanel } from "@/components/ui/section-bar";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { mensagemDaFalha } from "@/lib/mensagem-de-falha";
import { contagem } from "@/lib/plural";
import type { NotificationItem } from "@/lib/types";
import { IconButton } from "@/components/ui/icon-button";
import { QuadroToast } from "./kanban/QuadroToast";
import { useAcoesDoAviso } from "./notificacoes/use-acoes-do-aviso";
import {
  AcoesDoAviso,
  type AcoesDaLinha,
  GruposPorDia,
  LinhaDoAviso,
  LinkDoAviso,
  TituloDoAviso,
  aoTeclarNaLista,
  classeDoAlvo,
} from "./notificacoes/lista-de-avisos";

export { relativeTime } from "./notificacoes/lista-de-avisos";

// O sino abre com as mais recentes, e "Carregar mais" traz as anteriores de
// dez em dez. TUT-472 — o comentário antigo dizia que "a lista inteira mora na
// Central", mas o histórico da Central são os ENVIOS do escritório (e só para
// quem tem `notificacoes.configurar`), não as notificações da pessoa: o sino
// era o único lugar delas, e além das dez não havia caminho. TUT-517 — agora
// há: "Ver todas" abre a página `/notificacoes`.
const NO_SINO = 10;

/** A próxima página do sino, ou nenhuma quando o carregado já cobre o total. */
export function proximaPaginaDoSino(
  paginas: readonly { items: readonly unknown[]; total: number }[],
): number | undefined {
  const carregadas = paginas.reduce((soma, pagina) => soma + pagina.items.length, 0);
  const total = paginas.at(-1)?.total ?? 0;
  return carregadas < total ? paginas.length + 1 : undefined;
}

/**
 * O cache do sino cortado na primeira página. TUT-472 (Q024) — a consulta
 * infinita, ao rebuscar, refaz TODAS as páginas já carregadas, uma atrás da
 * outra: quem foi até a página N com "Carregar mais" passava a disparar N
 * pedidos a cada minuto pelo resto da sessão (o sino vive no AppShell, e as
 * páginas ficam no cache com o painel fechado), e outros N a cada "Marcar como
 * lida". Ao fechar, o sino volta às dez — que é o que o selo usa.
 */
export function soAPrimeiraPaginaDoSino<T>(
  dados: { pages: T[]; pageParams: unknown[] } | undefined,
): { pages: T[]; pageParams: unknown[] } | undefined {
  if (!dados || dados.pages.length <= 1) return dados;
  return { pages: dados.pages.slice(0, 1), pageParams: dados.pageParams.slice(0, 1) };
}

type FiltroDoSino = "nao-lidas" | "todas" | "arquivadas";

/**
 * TUT-586a — o sino abre um PAINEL LATERAL à direita, na altura toda (V1, a
 * prévia "A · Lista calma"), no lugar do popover: as abas Não lidas | Todas,
 * os avisos em grupos de dia e as ações no hover e no foco. Fecha pelo X, pelo
 * Esc, pelo sino e ao clicar fora (o véu do `Sheet` cobre o sino e o resto da
 * tela). TUT-586b — Adiar (1 hora, Amanhã, Próxima semana) e Arquivar, com
 * "Desfazer" no aviso, e a aba Arquivadas. A aba Adiadas mora só na página:
 * aqui o adiado aparece em "Todas", com a marca "Adiada · volta …".
 */
export function NotificationBell() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // TUT-517 — como no Inbox do Linear e nas notificações do GitHub, o sino abre
  // no que falta ler; "Todas" mostra o histórico.
  const [filtro, setFiltro] = useState<FiltroDoSino>("nao-lidas");
  // A linha que tinha o foco quando a ação a tirou da lista (em "Não lidas",
  // "Lida", "Adiar" e "Arquivar" somem com ela): o foco vai para a que ocupou
  // o lugar dela.
  const focoPendente = useRef<number | null>(null);
  const listaRef = useRef<HTMLDivElement>(null);
  const acoes = useAcoesDoAviso();
  const { falha } = acoes;

  const query = useInfiniteQuery({
    queryKey: ["notifications", "sino", filtro],
    // TUT-181 fatia 2b — o sino pedia o histórico INTEIRO a cada 60 s só para
    // desenhar dez linhas e um contador. Agora pede as dez, e o `unread`
    // continua vindo do CONJUNTO: é o contrato da porta, e é o que impede o
    // selo de virar "quantas não lidas cabem na tela".
    queryFn: ({ pageParam }) =>
      api.notifications(
        "",
        filtro === "nao-lidas",
        { page: pageParam, pageSize: NO_SINO },
        "",
        // TUT-586b — a aba Arquivadas. O adiado aparece em "Todas", com a marca.
        filtro === "arquivadas" ? "arquivadas" : undefined,
      ),
    initialPageParam: 1,
    getNextPageParam: (_ultima, paginas) => proximaPaginaDoSino(paginas),
    refetchInterval: 60_000,
  });
  const markAll = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: async () => {
      acoes.setFalha(null);
      await queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (erro: unknown) => acoes.setFalha(mensagemDaFalha(erro, "Não foi possível atualizar as notificações.")),
  });

  const unread = query.data?.pages[0]?.unread ?? 0;
  const items = query.data?.pages.flatMap((pagina) => pagina.items) ?? [];
  const total = query.data?.pages.at(-1)?.total ?? 0;

  useEffect(() => {
    const indice = focoPendente.current;
    if (indice === null || !listaRef.current) return;
    const alvos = listaRef.current.querySelectorAll<HTMLElement>("[data-alvo-do-aviso]");
    const alvo = alvos[Math.min(indice, alvos.length - 1)];
    // Lista vazia: o foco volta à aba, e não ao `<body>`.
    (alvo ?? document.getElementById(`sino-tab-${filtro}`))?.focus();
    focoPendente.current = null;
    // `query.data` e não `items`: a lista nova só existe quando o rebusca chega.
  }, [query.data, filtro]);
  // A ação que falhou não tira a linha: não há foco a mover.
  useEffect(() => {
    if (falha) focoPendente.current = null;
  }, [falha]);

  function fechar() {
    setOpen(false);
    queryClient.setQueryData(["notifications", "sino", filtro], soAPrimeiraPaginaDoSino);
  }

  function abrirAviso(item: NotificationItem) {
    acoes.marcarLida(item);
    fechar();
  }

  /** As ações da linha, guardando o lugar do foco quando ela vai sair da aba. */
  function acoesDaLinha(indice: number): AcoesDaLinha {
    const sai = (some: boolean) => {
      if (some) focoPendente.current = indice;
    };
    return {
      ocupado: acoes.ocupado,
      alternarLida: (item) => {
        sai(filtro === "nao-lidas" && !item.read);
        acoes.alternarLida(item);
      },
      adiar: (item, ate) => {
        sai(filtro === "nao-lidas");
        acoes.adiar(item, ate);
      },
      reativar: (item) => acoes.reativar(item),
      arquivar: (item) => {
        sai(true);
        acoes.arquivar(item);
      },
      restaurar: (item) => {
        sai(true);
        acoes.restaurar(item);
      },
    };
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(aberto) => {
        if (aberto) setOpen(true);
        else fechar();
      }}
    >
      {/* TUT-563f — o sino é o `IconButton` da casa; o selo mora no glifo (o
          nome acessível já diz a contagem). */}
      <SheetTrigger asChild>
        <IconButton
          label={
            unread > 0
              ? `Notificações: ${contagem(unread, "não lida", "não lidas")}`
              : "Notificações"
          }
          dica="Notificações"
          icone={
            <>
              <Bell />
              {unread > 0 ? (
                <span className="absolute -right-[0.15rem] -top-[0.15rem] h-[1.05rem] min-w-[1.05rem] rounded-full bg-orange px-[0.25rem] text-center text-legenda font-bold leading-[1.05rem] text-white">
                  {unread > 9 ? "9+" : unread}
                </span>
              ) : null}
            </>
          }
        />
      </SheetTrigger>
      {/* TUT-330 — ao abrir, o foco vai para o PRÓPRIO painel, e não para o
          primeiro controle: o Radix põe o foco no primeiro focável e acendia
          o halo laranja a cada clique no sino (dono, 27/09: "fica aquela
          marca laranja da caixa de texto"). Quem navega por teclado segue com
          Tab, e o Esc devolve o foco ao sino.

          A largura deixa uma faixa à esquerda no celular: é por ela que se
          "clica fora" (a e2e do tut-188), como na gaveta do menu. */}
      <SheetContent
        side="right"
        showCloseButton={false}
        overlayClassName="min-[640px]:bg-transparent"
        className="w-[min(26rem,calc(100vw-3rem))] gap-0 p-0 sm:max-w-none"
        tabIndex={-1}
        aria-label="Notificações"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement).focus();
        }}
      >
        <header className="flex items-start justify-between gap-2 px-4 pb-2 pt-4">
          <div className="min-w-0">
            <SheetTitle className="text-subtitulo">Notificações</SheetTitle>
            <SheetDescription className="text-legenda" aria-live="polite">
              {unread > 0 ? contagem(unread, "não lida", "não lidas") : "Tudo lido"}
            </SheetDescription>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="vidro"
              size="sm"
              disabled={unread === 0 || markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              <CheckCheck aria-hidden="true" />
              Marcar lidas
            </Button>
            <SheetClose asChild>
              <IconButton tamanho="sm" papel="contorno" label="Fechar notificações" dica="Fechar (Esc)" icone={<X />} />
            </SheetClose>
          </div>
        </header>
        <div className="border-b border-border px-4 pb-2.5">
          <SectionBar
            label="Quais notificações"
            idPrefix="sino"
            active={filtro}
            onSelect={(id) => setFiltro(id as FiltroDoSino)}
            sections={[
              { id: "nao-lidas", label: "Não lidas", ...(unread > 0 ? { count: unread } : {}) },
              { id: "todas", label: "Todas" },
              { id: "arquivadas", label: "Arquivadas" },
            ]}
          />
        </div>
        {falha ? (
          <p role="alert" className="m-0 px-4 pt-2 text-legenda text-destructive">
            {falha}
          </p>
        ) : null}
        <SectionPanel
          id={filtro}
          idPrefix="sino"
          active
          className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 pt-1.5"
        >
          <div ref={listaRef}>
            {items.length === 0 ? (
              <div className="grid justify-items-center gap-1 px-5 py-10 text-center text-[var(--muted)]">
                <Inbox aria-hidden="true" className="size-7" />
                <strong className="text-foreground">
                  {filtro === "nao-lidas" ? "Nada por ler" : filtro === "arquivadas" ? "Nada arquivado" : "Nenhum aviso"}
                </strong>
                <span className="text-legenda">
                  {filtro === "nao-lidas"
                    ? "Os avisos novos aparecem aqui."
                    : filtro === "arquivadas"
                      ? "Os avisos que você arquivar ficam guardados aqui."
                      : "Quando algo acontecer, aparece aqui."}
                </span>
              </div>
            ) : (
              <GruposPorDia
                items={items}
                renderLinha={(item) => (
                  <LinhaDoAviso
                    key={item.id}
                    item={item}
                    alvo={
                      <LinkDoAviso
                        item={item}
                        className={classeDoAlvo}
                        onClick={() => abrirAviso(item)}
                        onKeyDown={aoTeclarNaLista}
                        data-alvo-do-aviso
                      >
                        <TituloDoAviso item={item} />
                      </LinkDoAviso>
                    }
                    acoes={<AcoesDoAviso item={item} acoes={acoesDaLinha(items.indexOf(item))} />}
                  />
                )}
              />
            )}
          </div>
          {query.hasNextPage ? (
            <div className="flex justify-center pt-2">
              <Button
                type="button"
                variant="vidro"
                size="sm"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage
                  ? "Carregando…"
                  : `Carregar mais (${items.length} de ${total})`}
              </Button>
            </div>
          ) : null}
        </SectionPanel>
        {acoes.toast ? <QuadroToast toast={acoes.toast} onDismiss={acoes.fecharToast} /> : null}
        <footer className="flex justify-end border-t border-border px-4 py-2.5">
          <Button asChild variant="vidro" size="sm">
            <Link to="/notificacoes" onClick={fechar}>
              Ver todas
            </Link>
          </Button>
        </footer>
      </SheetContent>
    </Sheet>
  );
}
